import { useState } from 'react';
import { Plus, ChevronDown, Building2, FolderKanban } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useActiveOrganization } from '@/features/organizations/hooks/useActiveOrganization';
import { OrgAvatar } from '@/shared/components/custom/avatars/OrgAvatar';
import { OrganizationFormDialog } from '@/features/organizations/components/OrganizationFormDialog';
import { CreateProjectDialog } from '@/features/project-templates/public';
import { ROUTES } from '@/core/constants/routes';
import { useTranslation } from 'react-i18next';

export function OrganizationSwitcher() {
  const { t } = useTranslation('layout');
  const { activeOrganization, setActiveOrganization, organizations, activeOrganizationId } =
    useActiveOrganization();
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { orgId: currentOrgId } = useParams<{ orgId: string }>();
  const { pathname } = useLocation();

  const handleSelectOrg = (orgId: string) => {
    if (orgId === activeOrganizationId) return;
    setActiveOrganization(orgId);

    // Path segments after the current orgId, e.g. ['projects', 'proj-1', 'boards'].
    const rest = currentOrgId ? pathname.split('/').slice(2).filter(Boolean) : [];

    // Project- and client-scoped routes reference resource IDs that belong to the
    // previous organization and have no equivalent in the target one. Carrying the
    // path over would keep a stale board/dashboard visible, so land on members instead.
    const isResourceScoped = rest[0] === 'projects' || (rest[0] === 'clients' && rest.length > 1);

    navigate(
      isResourceScoped ? ROUTES.members(orgId) : `/${orgId}/${rest.join('/') || 'dashboard'}`,
    );
  };

  const isLoading = !activeOrganizationId && organizations.length === 0;

  return (
    <div className="py-3 px-3">
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 flex-1 px-2 py-2.5 rounded-lg bg-muted/40 hover:bg-primary/5 transition-colors group text-left"
            >
              {isLoading ? (
                <>
                  <Skeleton className="w-8 h-8 rounded-sm" />
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-24 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </>
              ) : (
                <>
                  <OrgAvatar
                    name={activeOrganization?.name || 'Org'}
                    color="purple-iris"
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate leading-none mb-0.5">
                      {activeOrganization?.name || t('sidebar.organization.select')}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {activeOrganization?.role || t('sidebar.organization.role')}
                    </p>
                  </div>
                </>
              )}
              <ChevronDown className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="top" align="start" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('sidebar.organization.title')}
              </p>
            </div>

            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => handleSelectOrg(org.id)}
                className="flex items-center gap-3 py-2"
              >
                <OrgAvatar name={org.name} color="purple-iris" size="sm" />
                <span className="flex-1 truncate">{org.name}</span>
                {org.id === activeOrganizationId && (
                  <span className="text-xs text-muted-foreground capitalize">{org.role}</span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-9 h-9 shrink-0"
                  aria-label={t('sidebar.organization.create')}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('sidebar.organization.create')}</TooltipContent>
          </Tooltip>

          <DropdownMenuContent side="top" align="end" className="w-52">
            <DropdownMenuItem className="gap-2" onClick={() => setOrgDialogOpen(true)}>
              <Building2 className="w-4 h-4 shrink-0" />
              {t('sidebar.organization.createOrganization')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2" onClick={() => setProjectDialogOpen(true)}>
              <FolderKanban className="w-4 h-4 shrink-0" />
              {t('sidebar.organization.createProject')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <OrganizationFormDialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen} />
      <CreateProjectDialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen} />
    </div>
  );
}
